import React, { useEffect, useState, useRef } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { WidgetsContext, Widget } from './../core';
import { useWidgetSelectorForType } from './../core/hooks';
import BackgroundView from '../background';
import Portal from '../../../components/Portal';
import { CarouselContext } from '../InteractiveContent';
import { HotspotRender } from '../../../components/hotspot';

import { HOTSPOT } from './../../../constants/widgetTypes';

import styles from './page.module.scss';

export const PageContext = React.createContext({
  zoom: 1,
});

const PageWidgetComponent = ({ className, style, widgetProps, breakpoint, children, storyboard }) => {
  const id = _.get(widgetProps, 'uid');
  const pageRef = useRef(null);
  const container = _.get(widgetProps, `properties.${breakpoint}.container`);
  const background = _.get(widgetProps, 'properties.desktop.background');
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`, {});
  const layoutWidth = parseInt(_.get(layout, 'width', '1200'));
  const context = React.useContext(WidgetsContext);
  const carouselContext = React.useContext(CarouselContext);
  const hotspotWidgets = useWidgetSelectorForType(HOTSPOT);

  const [zoom, setZoom] = useState(1);


  const storyboardType = _.get(context, 'storyboard.type', '');
  const activePage = _.get(carouselContext, 'activePage');

  useEffect(() => {
    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => {
      window.removeEventListener('resize', calculateZoom);
    }
  }, [storyboardType, breakpoint, layoutWidth]);

  const calculateZoom = () => {
    const windowWidth = document.body.clientWidth;
    var zoomValue = 1;
    if (context.shouldZoom) {
      zoomValue = windowWidth / container.minWidth;

      if (storyboardType !== 'INTERACTIVE_CONTENT' && windowWidth > 1200) {
        zoomValue = 1;
      }
    }

    setZoom(zoomValue);
  }

  var widgetStyle = {
    ...style,
    width: '100%',
    minWidth: container.minWidth
  }

  if (breakpoint === 'mobile') {
    var widgetStyle = {
      ...style,
      width: container.minWidth,
      height: layout.height
    }
  }

  widgetStyle = {
    ...widgetStyle,
    transform: `scale(${zoom})`
  }

  const pageContextValue = React.useMemo(() => ({
    zoom: zoom,
    pageWidth: container.minWidth
  }), [zoom, container.minWidth]);

  return (
    <PageContext.Provider value={pageContextValue}>
      <div id={id}
        className={cx(className, styles.page, 'cs_page_root', {
          [styles.mobile]: breakpoint === 'mobile',
          [styles.landingPage]: storyboardType !== 'INTERACTIVE_CONTENT'
        })}
        ref={pageRef}
        data-widget-id={id}
        data-container={true}
        style={widgetStyle}
      >
        <PageBackground id={id} breakpoint={breakpoint} context={context} ref={pageRef} storyboardType={storyboardType} background={background} activePage={activePage} />
        <HotspotRender breakpoint={breakpoint}>
          {children}
        </HotspotRender>
      </div>
    </PageContext.Provider>
  );
}

const PageBackground = React.forwardRef(({ id, breakpoint, context, background, storyboardType, activePage }, ref) => {
  if (!background || context.development) {
    return null;
  }

  if (storyboardType === 'INTERACTIVE_CONTENT') {

    const activeId = ref.current ? ref.current.parentElement.id : null;
    const activePageElement = ref.current ? ref.current.parentElement : null;
    const activePageParent = activePageElement ? activePageElement.parentElement : null;

    const { index, fullscreen } = activePage;

    useEffect(() => {
      const parentPage = document.getElementById(`page_${index}`)
      if (fullscreen && (activeId == index) && parentPage) {
        parentPage.style.backgroundColor = 'transparent';
      }
    }, [fullscreen, index])

    return (
      <React.Fragment key={id}>
        {(fullscreen && activeId == index) && breakpoint === 'desktop' &&
          <Portal>
            <BackgroundView background={background} breakpoint={breakpoint} />
          </Portal>
        }
        {(fullscreen && activeId == index) && (breakpoint === 'mobile' && activePageParent) &&
          <Portal mountNode={activePageParent}>
            <BackgroundView className={styles.pageBackgroundMobile}  background={background} breakpoint={breakpoint} />
          </Portal>
        }
        {!fullscreen &&
          <BackgroundView className={styles.pageBackground} background={background} breakpoint={breakpoint} />
        }
      </React.Fragment>
    )
  }

  return (
    <Portal >
      <BackgroundView
        className={cx(styles.landingPageBackground, {[styles.mobileLandingPageBackground]: breakpoint === 'mobile'})}
        background={background}
        breakpoint={breakpoint}
      />
    </Portal>
  )
})

const pageWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const PageWidget = React.memo(PageWidgetComponent, pageWidgetEquals);

